import { getHostDateLocale, hostCalendarLabel } from '@/shared/lib/format'

/** Local midnight of a `YYYY-MM-DD` value (null when empty or invalid). */
function parseLocalIso(iso: string): Date | null {
  if (!iso) return null
  const d = new Date(`${iso}T00:00:00`)
  return Number.isNaN(d.getTime()) ? null : d
}

/**
 * F-067: the trigger text for a picked date. AD comes from the LOCAL day the
 * value names (never a UTC parse, which shows the day before west of UTC);
 * a Nepal host whose calendar is BOTH or BS also sees the Bikram Sambat date
 * beside it, in the host calendar order. Display only: the value stays AD.
 */
export function datePickerLabel(iso: string): { primary: string; secondary: string | null } {
  const d = parseLocalIso(iso)
  if (!d) return { primary: iso, secondary: null }
  const ad = d.toLocaleDateString(getHostDateLocale(), {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
  return hostCalendarLabel(ad, d)
}

/** The one-string form of `datePickerLabel` ("Oct 8, 2026 (22 Ashwin 2083)"). */
export function datePickerText(iso: string): string {
  const { primary, secondary } = datePickerLabel(iso)
  return secondary ? `${primary} (${secondary})` : primary
}

/** Q4-BPOS9: the trigger's accessible name. Without one the button was named
 *  by its content, which ran the AD and BS dates together ("Oct 13, 202627
 *  Ashwin 2083"). The chosen date is spelled out with the BS date in
 *  brackets, after the field name when there is one. */
export function triggerName(
  name: string | undefined,
  iso: string | undefined | null,
): string | undefined {
  const text = iso ? datePickerText(iso) : ''
  if (name && text) return `${name}: ${text}`
  return name || text || undefined
}

/**
 * QA15-FD14: ARIA that ties the trigger to its portalled calendar. The panel
 * renders under <body>, so inside an aria-modal dialog it fell outside the
 * accessibility tree (no grid, no day buttons). aria-owns re-parents it, the
 * same fix DropdownSelect uses for its listbox (Q4-BPOS3). It goes on the
 * wrapper, never on the trigger <button>: a button's children are
 * presentational, so an owned calendar would lose its grid and day buttons.
 */
export function popupOwnership(
  open: boolean,
  panelId: string,
): { trigger: { 'aria-controls'?: string }; wrapper: { 'aria-owns'?: string } } {
  return open
    ? { trigger: { 'aria-controls': panelId }, wrapper: { 'aria-owns': panelId } }
    : { trigger: {}, wrapper: {} }
}
